import { useState, useEffect, useRef, useMemo, useCallback } from 'react';

interface CurtainTransitionProps {
  isActive: boolean;
  onMidpoint?: () => void;
  onComplete?: () => void;
  colors?: string[];
}

type Phase = 'idle' | 'closing' | 'closed' | 'opening' | 'complete';

export function CurtainTransition({
  isActive,
  onMidpoint,
  onComplete,
  colors,
}: CurtainTransitionProps) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);
  const animationRef = useRef<number | null>(null);
  const midpointCalledRef = useRef(false);

  // Use user's colors if provided, otherwise fallback to soft pastels
  const fallbackPalette = ['#f0c6c6', '#f5e0dc', '#dce5d3', '#cddaef', '#e0d4e8', '#f5e0dc'];
  const baseColors = colors && colors.length > 0 ? colors : fallbackPalette;
  
  // Soften colors by blending toward white - memoized for performance
  const palette = useMemo(() => {
    const softenColor = (hex: string): string => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      // Blend 30% toward white for softer feel
      const soften = (c: number) => Math.round(c + (255 - c) * 0.3);
      return `#${soften(r).toString(16).padStart(2, '0')}${soften(g).toString(16).padStart(2, '0')}${soften(b).toString(16).padStart(2, '0')}`;
    };
    
    const softColors = baseColors.map(softenColor);
    
    // Expand colors to create smooth gradient (repeat to fill 6 slots)
    return softColors.length >= 6 
      ? softColors.slice(0, 6)
      : [...softColors, ...softColors, ...softColors].slice(0, 6);
  }, [baseColors]);

  // Build gradient string - memoized
  const baseGradient = useMemo(() => {
    const stops = palette.map((color, i) => {
      const percent = (i / (palette.length - 1)) * 100;
      return `${color} ${percent}%`;
    });
    return `linear-gradient(to bottom, ${stops.join(', ')})`;
  }, [palette]);

  const startOpenAnimation = useCallback(() => {
    const startTime = performance.now();
    const openDuration = 600;

    const animateOpen = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const rawProgress = Math.min(elapsed / openDuration, 1);
      const eased = 1 - Math.pow(1 - rawProgress, 3);
      setProgress(1 - eased);

      if (rawProgress < 1) {
        animationRef.current = requestAnimationFrame(animateOpen);
      } else {
        setPhase('complete');
        setProgress(0);
        onComplete?.();
        setTimeout(() => setPhase('idle'), 50);
      }
    };

    animationRef.current = requestAnimationFrame(animateOpen);
  }, [onComplete]);

  useEffect(() => {
    if (isActive && phase === 'idle') {
      setPhase('closing');
      setProgress(0);
      midpointCalledRef.current = false;

      const startTime = performance.now();
      const closeDuration = 550;

      const animateClose = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const rawProgress = Math.min(elapsed / closeDuration, 1);
        const eased = 1 - Math.pow(1 - rawProgress, 2);
        setProgress(eased);

        if (rawProgress < 1) {
          animationRef.current = requestAnimationFrame(animateClose);
        } else {
          setPhase('closed');
          if (!midpointCalledRef.current && onMidpoint) {
            midpointCalledRef.current = true;
            onMidpoint();
          }
          
          setTimeout(() => {
            setPhase('opening');
            startOpenAnimation();
          }, 150);
        }
      };

      animationRef.current = requestAnimationFrame(animateClose);
    }
  }, [isActive, phase, onMidpoint, startOpenAnimation]);

  useEffect(() => {
    if (!isActive && phase !== 'idle' && phase !== 'complete') {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      setPhase('idle');
      setProgress(0);
    }
  }, [isActive, phase]);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  if (phase === 'idle' || phase === 'complete') {
    return null;
  }

  // Use transform for GPU-accelerated animation instead of width
  // Curtains start off-screen and slide in
  const leftTransform = `translateX(${(progress - 1) * 100}%)`;
  const rightTransform = `translateX(${(1 - progress) * 100}%)`;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {/* Left curtain - GPU-accelerated with transform */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: '52vw',
          transform: leftTransform,
          willChange: 'transform',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          overflow: 'hidden',
        }}
      >
        {/* Blurred gradient - static, not animated */}
        <div
          style={{
            position: 'absolute',
            top: '-20px',
            left: '-20px',
            right: '-20px',
            bottom: '-20px',
            background: baseGradient,
            filter: 'blur(20px)',
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
          }}
        />
        {/* Soft fold highlights */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `repeating-linear-gradient(
              90deg,
              transparent 0px,
              transparent 45px,
              rgba(255,255,255,0.12) 75px,
              transparent 105px,
              transparent 150px
            )`,
            transform: 'translateZ(0)',
          }}
        />
        {/* Soft fold shadows */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `repeating-linear-gradient(
              90deg,
              transparent 0px,
              transparent 60px,
              rgba(0,0,0,0.06) 90px,
              transparent 120px,
              transparent 180px
            )`,
            transform: 'translateZ(0)',
          }}
        />
        {/* Inner edge shadow */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: '25px',
            background: 'linear-gradient(to right, transparent, rgba(0,0,0,0.12))',
          }}
        />
      </div>

      {/* Right curtain - GPU-accelerated with transform */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: '52vw',
          transform: rightTransform,
          willChange: 'transform',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          overflow: 'hidden',
        }}
      >
        {/* Blurred gradient - static, not animated */}
        <div
          style={{
            position: 'absolute',
            top: '-20px',
            left: '-20px',
            right: '-20px',
            bottom: '-20px',
            background: baseGradient,
            filter: 'blur(20px)',
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
          }}
        />
        {/* Soft fold highlights */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `repeating-linear-gradient(
              90deg,
              transparent 0px,
              transparent 45px,
              rgba(255,255,255,0.12) 75px,
              transparent 105px,
              transparent 150px
            )`,
            transform: 'translateZ(0)',
          }}
        />
        {/* Soft fold shadows */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `repeating-linear-gradient(
              90deg,
              transparent 0px,
              transparent 60px,
              rgba(0,0,0,0.06) 90px,
              transparent 120px,
              transparent 180px
            )`,
            transform: 'translateZ(0)',
          }}
        />
        {/* Inner edge shadow */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: '25px',
            background: 'linear-gradient(to left, transparent, rgba(0,0,0,0.12))',
          }}
        />
      </div>
    </div>
  );
}
